import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useInView } from 'react-intersection-observer'
import { rem } from 'polished'
import styled from 'styled-components'

import ExperienceOfferCard from 'components/OfferList/OfferCards/ExperienceOfferCard'
import OfferCardLoadingSkeleton from 'components/OfferList/OfferCards/OfferCardLoadingSkeleton'
import { getExperiencesPageLocations } from 'components/Experiences/ExperiencesPage/ExperiencesPageHardcodedLists'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { CAROUSEL_IN_VIEW_PARAMS } from 'home/constants'
import OfferCarouselLookingForSomethingElse from 'home/components/LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import FullWidthCarouselWithAnalytics from 'components/WithAnalytics/Carousel/FullWidthCarouselWithAnalytics'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import { getDefaultRegionPlace } from 'constants/places'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import { useLocalizedPlaces, useResolvedPlaceIdentity } from 'hooks/usePlace'
import { useAppSelector } from 'hooks/reduxHooks'
import { take } from 'lib/array/arrayUtils'
import { setLogicalScrollLeft } from 'LuxKit/Carousel/utils'
import LoyaltySpendPointsPricingContext from 'loyalty/contexts/LoyaltySpendPointsPricingContext'
import LoyaltySpendPointsCarouselSection from './LoyaltySpendPointsCarouselSection'
import type { SpendPointsSortOption } from './loyaltySpendPointsTypes'
import { DESTINATION_AVAILABILITY_PROBE_LIMIT, MAX_CAROUSEL_FILTER_OPTIONS, MINIMUM_OFFERS_TO_SHOW_CAROUSEL, SPEND_POINTS_CAROUSEL_OFFER_LIMIT, SPEND_POINTS_CAROUSEL_RENDER_LIMIT } from './loyaltySpendPointsConstants'
import useLoyaltySpendPointsFilterAvailability from './useLoyaltySpendPointsFilterAvailability'
import useLoyaltySpendPointsExperienceBurnFilter from './useLoyaltySpendPointsExperienceBurnFilter'

// Points-based sort options use a matching cash-price sort to fetch the right end of
// the catalog; client-side re-sort by priceInPoints is applied in useLoyaltySpendPointsExperienceBurnFilter.
const SORT_BY_OPTION: Record<SpendPointsSortOption, Experiences.SortTypes | undefined> = {
  recommended: undefined,
  'lowest-points': 'price.asc',
  'highest-points': 'price.desc',
}

// Match CarouselCardMedium min-height on tablet+ for this carousel only.
const ExperienceCarouselCardSkeleton = styled(OfferCardLoadingSkeleton)`
  ${mediaQueryUp.tablet} {
    min-height: ${rem(520)};
  }
`

const ExperienceCarouselCardSlot = styled.div`
  flex-shrink: 0;
  display: flex;

  ${mediaQueryUp.tablet} {
    min-height: ${rem(520)};
  }
`

interface Props {
  title: ReactNode
  sortOption: SpendPointsSortOption
}

interface ExperienceFilterPrefetchProps {
  value: string
  sortBy?: Experiences.SortTypes
  cashPriceCap: number
  onAvailabilityChange: (value: string, isAvailable: boolean) => void
}

function ExperienceFilterPrefetch(props: ExperienceFilterPrefetchProps) {
  const { value, sortBy, cashPriceCap, onAvailabilityChange } = props
  const list = useExperienceSearchList({
    placeId: value,
    enabled: true,
    sortBy,
    priceLte: cashPriceCap,
    limit: DESTINATION_AVAILABILITY_PROBE_LIMIT,
    streamingEnabled: true,
  })

  useEffect(() => {
    if (!list.fetching) {
      onAvailabilityChange(value, list.ids.length >= MINIMUM_OFFERS_TO_SHOW_CAROUSEL)
    }
  }, [list.fetching, list.ids, onAvailabilityChange, value])

  return null
}

function LoyaltySpendPointsExperienceCarousel(props: Props) {
  const { title, sortOption } = props
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const currentRegionName = useAppSelector(state => state.geo.currentRegionName)
  const { showOffersWithinBalance } = useContext(LoyaltySpendPointsPricingContext)

  const regionPlaceIdentity = useMemo(
    () => getDefaultRegionPlace(currentRegionCode),
    [currentRegionCode],
  )
  const resolvedRegionPlace = useResolvedPlaceIdentity(regionPlaceIdentity)
  const regionPlaceId = regionPlaceIdentity.id
  const regionFilterLabel = currentRegionName || resolvedRegionPlace?.name || ''

  const [selectedDestination, setSelectedDestination] = useState(regionPlaceId)
  const [cashPriceCap, setCashPriceCap] = useState<number>()

  const regionalLocations = useMemo(
    () => getExperiencesPageLocations(currentRegionCode.toLowerCase()),
    [currentRegionCode],
  )
  const regionalLocationPlaceIds = useMemo(
    () => regionalLocations.map(location => location.place.id),
    [regionalLocations],
  )
  const localizedRegionalPlaces = useLocalizedPlaces(regionalLocationPlaceIds)

  const filterOptions = useMemo(() => {
    if (regionalLocations.length === 0) {
      return []
    }

    const regionalOptions = regionalLocations
      .filter(({ place }) => place.id !== regionPlaceId)
      .slice(0, MAX_CAROUSEL_FILTER_OPTIONS - 1)
      .flatMap(({ place }) => {
        const name = localizedRegionalPlaces[place.id]?.place?.name
        if (!name) return []
        return [{
          value: place.id,
          label: name,
        }]
      })

    return [
      {
        value: regionPlaceId,
        label: regionFilterLabel,
      },
      ...regionalOptions,
    ]
  }, [localizedRegionalPlaces, regionFilterLabel, regionPlaceId, regionalLocations])

  const onDestinationChange = useCallback((destination: string) => {
    setSelectedDestination(currentDestination => (
      currentDestination === destination ? regionPlaceId : destination
    ))
  }, [regionPlaceId])

  useEffect(() => {
    setSelectedDestination(regionPlaceId)
  }, [regionPlaceId])

  useEffect(() => {
    setCashPriceCap(undefined)
  }, [showOffersWithinBalance])

  const list = useExperienceSearchList({
    placeId: selectedDestination,
    enabled: !!selectedDestination,
    sortBy: SORT_BY_OPTION[sortOption],
    priceLte: showOffersWithinBalance && cashPriceCap !== undefined
      ? cashPriceCap
      : undefined,
    limit: SPEND_POINTS_CAROUSEL_OFFER_LIMIT,
    streamingEnabled: true,
  })
  const experienceIdsForBurnFilter = take(list.ids, SPEND_POINTS_CAROUSEL_OFFER_LIMIT)
  const burnFilteredList = useLoyaltySpendPointsExperienceBurnFilter({
    experienceIds: experienceIdsForBurnFilter,
    searchFetching: list.fetching,
    sortOption,
  })
  const isCarouselFetching = burnFilteredList.fetching
  const renderLimit = SPEND_POINTS_CAROUSEL_RENDER_LIMIT
  const visibleExperienceIds = useMemo(() => (
    take(burnFilteredList.experienceIds, renderLimit)
  ), [burnFilteredList.experienceIds, renderLimit])
  const showCarouselSkeletons = visibleExperienceIds.length === 0 && (list.fetching || isCarouselFetching)

  useEffect(() => {
    if (burnFilteredList.cashPriceCap !== undefined) {
      setCashPriceCap(currentCap => Math.max(currentCap ?? 0, burnFilteredList.cashPriceCap!))
    }
  }, [burnFilteredList.cashPriceCap])

  const carouselRef = useRef<HTMLDivElement | null>(null)
  const [inViewRef, inView] = useInView(CAROUSEL_IN_VIEW_PARAMS)
  const onCarouselRef = useCallback((node: HTMLDivElement | null) => {
    inViewRef(node)
    carouselRef.current = node
  }, [inViewRef])

  useEffect(() => {
    if (!isCarouselFetching && carouselRef.current) {
      setLogicalScrollLeft(carouselRef.current, 0)
    }
  }, [isCarouselFetching, list.listKey, sortOption])

  const { visibleOptions: visibleFilterOptions, onAvailabilityChange: onDestinationAvailabilityChange, isResolved: areDestinationFiltersResolved, shouldShowFilters } = useLoyaltySpendPointsFilterAvailability({
    options: filterOptions,
    selectedValue: selectedDestination,
    defaultValue: regionPlaceId,
    enabled: showOffersWithinBalance,
    availabilityKey: cashPriceCap?.toString(),
    onSelectedValueChange: setSelectedDestination,
  })

  if (showOffersWithinBalance && selectedDestination === regionPlaceId && !isCarouselFetching && visibleExperienceIds.length < MINIMUM_OFFERS_TO_SHOW_CAROUSEL) {
    return null
  }

  return <>
    {showOffersWithinBalance && cashPriceCap !== undefined && filterOptions
      .filter(option => option.value !== regionPlaceId)
      .map(option => <ExperienceFilterPrefetch
        key={option.value}
        value={option.value}
        sortBy={SORT_BY_OPTION[sortOption]}
        cashPriceCap={cashPriceCap}
        onAvailabilityChange={onDestinationAvailabilityChange}
      />)}
    <LoyaltySpendPointsCarouselSection
      title={title}
      viewAllTo="/experiences"
      filterOptions={visibleFilterOptions}
      selectedFilter={selectedDestination}
      onFilterChange={onDestinationChange}
      showFilters={shouldShowFilters}
      filtersLoading={showOffersWithinBalance && shouldShowFilters && !areDestinationFiltersResolved}
      testId="loyalty-spend-points-experiences-carousel"
    >
      <FullWidthCarouselWithAnalytics snap="start" itemsPerArrow={3} ref={onCarouselRef}>
        <LoaderPlayStateContext.Provider value={inView ? 'running' : 'paused'}>
          {showCarouselSkeletons && <>
            <ExperienceCarouselCardSkeleton variant="card-default" size="medium" />
            <ExperienceCarouselCardSkeleton variant="card-default" size="medium" />
            <ExperienceCarouselCardSkeleton variant="card-default" size="medium" />
            <ExperienceCarouselCardSkeleton variant="card-default" size="medium" />
          </>}
          {visibleExperienceIds.map((id, index) => (
            <ExperienceCarouselCardSlot key={id}>
              <ExperienceOfferCard
                id={id}
                index={index}
                lazyLoad={index > 1}
                size="medium"
                viewPriceInPoints
              />
            </ExperienceCarouselCardSlot>
          ))}
          <OfferCarouselLookingForSomethingElse to="/experiences" />
        </LoaderPlayStateContext.Provider>
      </FullWidthCarouselWithAnalytics>
    </LoyaltySpendPointsCarouselSection>
  </>
}

export default LoyaltySpendPointsExperienceCarousel
